import React, { useState, useEffect } from 'react';
import { 
  Edit2, Save, X, History, Clock, User, AlertTriangle, CheckCircle2
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from './ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Edit Transaction Sheet
export const TransactionEditSheet = ({ 
  transaction, 
  products, 
  vendors, 
  isOpen, 
  onClose, 
  onSuccess 
}) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    product_id: '',
    vendor_id: '',
    transaction_type: 'in',
    quantity: 0,
    rate: 0,
    notes: '',
    driver_name: '',
    driver_contact: '',
    vehicle_number: '',
    transaction_date: ''
  });

  useEffect(() => {
    if (transaction) {
      setFormData({
        product_id: transaction.product_id || '',
        vendor_id: transaction.vendor_id || '',
        transaction_type: transaction.transaction_type || 'in',
        quantity: transaction.quantity || 0,
        rate: transaction.rate || 0,
        notes: transaction.notes || '',
        driver_name: transaction.driver_name || '',
        driver_contact: transaction.driver_contact || '',
        vehicle_number: transaction.vehicle_number || '',
        transaction_date: transaction.transaction_date 
          ? new Date(transaction.transaction_date).toISOString().slice(0, 16) 
          : ''
      });
    }
  }, [transaction]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!transaction) return;

    setLoading(true);
    try {
      // Build update object with only changed fields
      const updateData = {};
      
      if (formData.product_id !== transaction.product_id) {
        updateData.product_id = formData.product_id;
      }
      if (formData.vendor_id !== transaction.vendor_id) {
        updateData.vendor_id = formData.vendor_id;
      }
      if (formData.transaction_type !== transaction.transaction_type) {
        updateData.transaction_type = formData.transaction_type;
      }
      if (formData.quantity !== transaction.quantity) {
        updateData.quantity = parseFloat(formData.quantity);
      }
      if (formData.rate !== (transaction.rate || 0)) {
        updateData.rate = parseFloat(formData.rate);
      }
      if (formData.notes !== (transaction.notes || '')) {
        updateData.notes = formData.notes;
      }
      if (formData.driver_name !== (transaction.driver_name || '')) {
        updateData.driver_name = formData.driver_name;
      }
      if (formData.driver_contact !== (transaction.driver_contact || '')) {
        updateData.driver_contact = formData.driver_contact;
      }
      if (formData.vehicle_number !== (transaction.vehicle_number || '')) {
        updateData.vehicle_number = formData.vehicle_number;
      }
      if (formData.transaction_date) {
        const newDate = new Date(formData.transaction_date).toISOString();
        if (newDate !== transaction.transaction_date) {
          updateData.transaction_date = newDate;
        }
      }

      if (Object.keys(updateData).length === 0) {
        toast.info("No changes to save");
        return;
      }

      // PUT returns the updated transaction
      const response = await axios.put(
        `${API}/api/transactions/${transaction.id}`,
        updateData,
        { withCredentials: true }
      );

      toast.success("Transaction updated successfully");
      // Pass the updated transaction back to parent for in-place update
      if (onSuccess) onSuccess(response.data);
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update transaction");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-[500px] sm:max-w-[500px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Edit2 className="w-5 h-5" />
            Edit Transaction
          </SheetTitle>
          <SheetDescription>
            Modify transaction details. Changes will be tracked in audit history.
          </SheetDescription>
        </SheetHeader>

        {transaction?.is_edited && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
            <History className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-800">
              This transaction has been edited {transaction.edit_history?.length || 0} time(s)
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4 mt-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Product *</Label>
              <Select 
                value={formData.product_id} 
                onValueChange={(value) => setFormData({...formData, product_id: value})}
              >
                <SelectTrigger data-testid="edit-product-select">
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.product_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Vendor *</Label>
              <Select 
                value={formData.vendor_id} 
                onValueChange={(value) => setFormData({...formData, vendor_id: value})}
              >
                <SelectTrigger data-testid="edit-vendor-select">
                  <SelectValue placeholder="Select vendor" />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map(v => (
                    <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Transaction Type *</Label>
              <Select 
                value={formData.transaction_type} 
                onValueChange={(value) => setFormData({...formData, transaction_type: value})}
              >
                <SelectTrigger data-testid="edit-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="in">Stock In</SelectItem>
                  <SelectItem value="out">Stock Out</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Quantity (kg) *</Label>
              <Input
                type="number"
                step="0.01"
                min="0.01"
                value={formData.quantity}
                onChange={(e) => setFormData({...formData, quantity: parseFloat(e.target.value) || 0})}
                data-testid="edit-quantity-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Rate (₹/kg)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.rate}
                onChange={(e) => setFormData({...formData, rate: parseFloat(e.target.value) || 0})}
                data-testid="edit-rate-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Date & Time</Label>
              <Input
                type="datetime-local"
                value={formData.transaction_date}
                onChange={(e) => setFormData({...formData, transaction_date: e.target.value})}
                data-testid="edit-date-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>Driver Name</Label>
              <Input
                value={formData.driver_name}
                onChange={(e) => setFormData({...formData, driver_name: e.target.value})}
                data-testid="edit-driver-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Driver Contact</Label>
              <Input
                value={formData.driver_contact}
                onChange={(e) => setFormData({...formData, driver_contact: e.target.value})}
                data-testid="edit-driver-contact-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Vehicle Number</Label>
              <Input
                value={formData.vehicle_number}
                onChange={(e) => setFormData({...formData, vehicle_number: e.target.value})}
                data-testid="edit-vehicle-input"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Notes</Label>
            <Textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Additional notes..."
              rows={3}
              data-testid="edit-notes-input"
            />
          </div>

          <SheetFooter className="mt-6">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="bg-lime-700 hover:bg-lime-800"
              disabled={loading}
              data-testid="save-edit-btn"
            >
              {loading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
};

// Edit History Dialog
export const TransactionHistoryDialog = ({ transaction, isOpen, onClose }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && transaction) {
      fetchHistory();
    }
  }, [isOpen, transaction]);

  const fetchHistory = async () => {
    if (!transaction) return;
    
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/api/transactions/${transaction.id}/history`,
        { withCredentials: true }
      );
      setHistory(response.data.edit_history || []);
    } catch (error) {
      toast.error("Failed to fetch edit history");
    } finally {
      setLoading(false);
    }
  };

  const formatValue = (key, value) => {
    if (key.includes('date') && value) {
      return new Date(value).toLocaleString();
    }
    if (typeof value === 'number') {
      return value.toFixed(2);
    }
    return value || '—';
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Transaction Edit History
          </DialogTitle>
          <DialogDescription>
            Complete audit trail of all modifications to this transaction
          </DialogDescription>
        </DialogHeader>

        <div className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-lime-700" />
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8 text-stone-500">
              <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-lime-500" />
              <p>No edit history</p>
              <p className="text-sm">This transaction has not been modified since creation</p>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((entry, idx) => (
                <Card key={idx} className="border-stone-200">
                  <CardHeader className="pb-2">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-stone-500" />
                        <span className="font-medium">{entry.edited_by_name}</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm text-stone-500">
                        <Clock className="w-3 h-3" />
                        {new Date(entry.edit_date).toLocaleString()}
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="font-medium text-stone-600 mb-2">Original Values</p>
                        <div className="space-y-1 p-2 bg-rose-50 rounded border border-rose-200">
                          {Object.entries(entry.original_values || {}).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-stone-500">{key.replace(/_/g, ' ')}:</span>
                              <span className="text-rose-700">{formatValue(key, value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div>
                        <p className="font-medium text-stone-600 mb-2">New Values</p>
                        <div className="space-y-1 p-2 bg-lime-50 rounded border border-lime-200">
                          {Object.entries(entry.new_values || {}).map(([key, value]) => (
                            <div key={key} className="flex justify-between">
                              <span className="text-stone-500">{key.replace(/_/g, ' ')}:</span>
                              <span className="text-lime-700">{formatValue(key, value)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Edited Badge Component
export const EditedBadge = ({ transaction, onClick }) => {
  if (!transaction?.is_edited) return null;
  
  return (
    <button
      onClick={onClick}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800 border border-amber-200 hover:bg-amber-200 transition-colors"
      title="View edit history"
      data-testid={`edited-badge-${transaction.id}`}
    >
      <Edit2 className="w-3 h-3" />
      Edited
    </button>
  );
};

export default TransactionEditSheet;
